import React, { useEffect, useState } from "react"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Separator } from "@/components/ui/separator"
import { StatusBadge } from "@/components/ui/status-badge"
import { EmptyState } from "@/components/ui/empty-state"
import {
  AlertTriangle,
  ExternalLink,
  Phone,
  PhoneCall,
  PhoneOff,
  Voicemail,
} from "lucide-react"
import { Link } from "react-router-dom"
import { cn } from "@/lib/utils"
import type { DemoCallState, DemoCallStatus } from "../types"
import { timestampMs } from "@/lib/datetime"

interface LiveCallPanelProps {
  status: DemoCallStatus | undefined
  dialing: boolean
  error: string | null
  onReset: () => void
}

type Tone = "live" | "good" | "warn" | "bad" | "idle"

const STATE_COPY: Record<
  DemoCallState,
  { label: string; hint: string; tone: Tone }
> = {
  PENDING: {
    label: "Ringing",
    hint: "The phone should be lighting up now.",
    tone: "live",
  },
  IN_PROGRESS: {
    label: "On the call",
    hint: "The agent is talking with them.",
    tone: "live",
  },
  COMPLETED: {
    label: "Call finished",
    hint: "Transcript and summary land within a few seconds.",
    tone: "good",
  },
  VOICEMAIL: {
    label: "Went to voicemail",
    hint: "The agent left the voicemail message and hung up.",
    tone: "warn",
  },
  FAILED: {
    label: "Call failed",
    hint: "The call never connected.",
    tone: "bad",
  },
  CANCELLED: {
    label: "Cancelled",
    hint: "The call was cancelled before it went out.",
    tone: "warn",
  },
}

const TONE_STYLES: Record<Tone, { ring: string; dot: string; text: string }> = {
  live: {
    ring: "bg-info/10 ring-info/30",
    dot: "bg-info",
    text: "text-info",
  },
  good: {
    ring: "bg-success/10 ring-success/30",
    dot: "bg-success",
    text: "text-success",
  },
  warn: {
    ring: "bg-warning/10 ring-warning/30",
    dot: "bg-warning",
    text: "text-warning",
  },
  bad: {
    ring: "bg-destructive/10 ring-destructive/30",
    dot: "bg-destructive",
    text: "text-destructive",
  },
  idle: {
    ring: "bg-muted ring-border",
    dot: "bg-muted-foreground",
    text: "text-muted-foreground",
  },
}

const isLive = (state: DemoCallState) =>
  state === "PENDING" || state === "IN_PROGRESS"

function formatDuration(totalSeconds: number): string {
  const mins = Math.floor(totalSeconds / 60)
  const secs = totalSeconds % 60
  return `${mins}:${secs.toString().padStart(2, "0")}`
}

/** Counts up from the moment the call connected, so the panel feels live. */
function useElapsed(startedAt: string | null | undefined, running: boolean) {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (!running) return
    const id = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(id)
  }, [running])
  if (!startedAt) return null
  const started = timestampMs(startedAt)
  if (Number.isNaN(started)) return null
  return Math.max(0, Math.floor((now - started) / 1000))
}

function TranscriptTurn({
  role,
  content,
}: {
  role?: string
  content?: string
}) {
  if (!content) return null
  const isAgent = role === "agent" || role === "assistant"
  return (
    <div className={cn("flex", isAgent ? "justify-start" : "justify-end")}>
      <div
        className={cn(
          "max-w-[85%] rounded-lg px-3 py-2 text-sm",
          isAgent
            ? "bg-muted text-foreground"
            : "bg-pear-teal/10 text-foreground"
        )}
      >
        <p className="mb-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
          {isAgent ? "Agent" : "Patient"}
        </p>
        {content}
      </div>
    </div>
  )
}

export function LiveCallPanel({
  status,
  dialing,
  error,
  onReset,
}: LiveCallPanelProps) {
  const live = status ? isLive(status.status) : false
  const elapsed = useElapsed(status?.started_at, live)

  if (error) {
    return (
      <Card className="border-destructive/40">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" />
            Couldn't place the call
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">{error}</p>
          <Button variant="outline" onClick={onReset}>
            Try again
          </Button>
        </CardContent>
      </Card>
    )
  }

  if (dialing && !status) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center gap-3 py-16 text-center">
          <span className="relative flex h-14 w-14 items-center justify-center rounded-full bg-info/10 ring-4 ring-info/20">
            <span className="absolute inset-0 animate-ping rounded-full bg-info/20" />
            <PhoneCall className="h-6 w-6 text-info" />
          </span>
          <p className="font-medium">Setting up the call</p>
          <p className="max-w-xs text-sm text-muted-foreground">
            Building the demo patient and briefing the agent. Check-in calls
            take a little longer while the script is written.
          </p>
        </CardContent>
      </Card>
    )
  }

  if (!status) {
    return (
      <EmptyState
        icon={Phone}
        title="No call yet"
        description="Fill in the form and hit Call now. This panel follows the call live and shows the transcript when it ends."
        className="h-full min-h-[320px]"
      />
    )
  }

  const copy = STATE_COPY[status.status]
  const tone = TONE_STYLES[copy?.tone ?? "idle"]
  const duration =
    status.duration_seconds ?? (live && elapsed !== null ? elapsed : null)

  return (
    <Card>
      <CardHeader className="pb-4">
        <div className="flex flex-wrap items-start justify-between gap-3 sm:gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <span
              className={cn(
                "relative flex h-11 w-11 items-center justify-center rounded-full ring-4",
                tone.ring
              )}
            >
              {live && (
                <span
                  className={cn(
                    "absolute inset-0 animate-ping rounded-full opacity-40",
                    tone.dot
                  )}
                />
              )}
              {status.status === "VOICEMAIL" ? (
                <Voicemail className={cn("h-5 w-5", tone.text)} />
              ) : status.status === "FAILED" ? (
                <PhoneOff className={cn("h-5 w-5", tone.text)} />
              ) : (
                <PhoneCall className={cn("h-5 w-5", tone.text)} />
              )}
            </span>
            <div>
              <CardTitle className="text-lg">
                {copy?.label ?? status.status}
              </CardTitle>
              <CardDescription>{copy?.hint}</CardDescription>
            </div>
          </div>
          {duration !== null && (
            <span className="font-mono text-2xl tabular-nums text-muted-foreground">
              {formatDuration(duration)}
            </span>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
          <div>
            <dt className="text-xs text-muted-foreground">Calling</dt>
            <dd className="font-medium">{status.to_number}</dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">As</dt>
            <dd className="font-medium">{status.persona_name ?? "—"}</dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">Agent</dt>
            <dd className="font-medium">
              {status.strategy_type === "CHECK_IN" ? "Check-in" : "Consent"}
            </dd>
          </div>
          {status.user_sentiment && (
            <div>
              <dt className="text-xs text-muted-foreground">Sentiment</dt>
              <dd>
                <StatusBadge
                  variant={
                    status.user_sentiment.toLowerCase() === "positive"
                      ? "success"
                      : status.user_sentiment.toLowerCase() === "negative"
                        ? "destructive"
                        : "muted"
                  }
                  dot
                >
                  {status.user_sentiment}
                </StatusBadge>
              </dd>
            </div>
          )}
        </dl>

        {status.failure_reason && (
          <p className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
            {status.failure_reason}
          </p>
        )}

        {status.summary && (
          <>
            <Separator />
            <div className="space-y-1.5">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Summary
              </p>
              <p className="text-sm leading-relaxed">{status.summary}</p>
            </div>
          </>
        )}

        {status.transcript.length > 0 && (
          <>
            <Separator />
            <div className="space-y-2">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Transcript
              </p>
              <ScrollArea className="h-64 rounded-md border p-3">
                <div className="space-y-2">
                  {status.transcript.map((turn, i) => (
                    <TranscriptTurn
                      key={i}
                      role={turn.role as string | undefined}
                      content={turn.content as string | undefined}
                    />
                  ))}
                </div>
              </ScrollArea>
            </div>
          </>
        )}

        <div className="flex flex-wrap items-center gap-2 pt-1">
          <Button variant="outline" size="sm" asChild>
            <Link to={`/calls/${status.call_id}`}>
              <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
              Full call record
            </Link>
          </Button>
          {status.recording_url && (
            <Button variant="ghost" size="sm" asChild>
              <a
                href={status.recording_url}
                target="_blank"
                rel="noreferrer noopener"
              >
                Recording
              </a>
            </Button>
          )}
          {!live && (
            <Button variant="ghost" size="sm" onClick={onReset}>
              New demo
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
